import { useState, useEffect } from 'react';
import { db } from '../db';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';

const HistoryScreen = () => {
  const [expenses, setExpenses] = useState([]);
  const [weeklyTotals, setWeeklyTotals] = useState([]);
  const [categoryTotals, setCategoryTotals] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      // Get all expenses that are not deleted
      const allExpenses = await db.expenses
        .orderBy('date')
        .reverse()
        .toArray();
      
      // Filter out deleted expenses
      const validExpenses = allExpenses.filter(exp => !exp.deleted);

      setExpenses(validExpenses);

      // Calculate weekly totals
      const weeks = {};
      const now = new Date();
      
      for (let i = 0; i < 4; i++) {
        const weekStart = startOfWeek(subWeeks(now, i));
        const weekEnd = endOfWeek(subWeeks(now, i));
        const weekKey = format(weekStart, 'MMM dd');
        weeks[weekKey] = { start: weekStart, end: weekEnd, total: 0 };
      }

      validExpenses.forEach(expense => {
        const expenseDate = new Date(expense.date);
        Object.keys(weeks).forEach(key => {
          if (expenseDate >= weeks[key].start && expenseDate <= weeks[key].end) {
            weeks[key].total += expense.amount;
          }
        });
      });

      setWeeklyTotals(Object.entries(weeks).map(([week, data]) => ({
        week,
        total: data.total
      })));

      // Calculate category totals
      const categories = {};
      validExpenses.forEach(expense => {
        categories[expense.category] = (categories[expense.category] || 0) + expense.amount;
      });

      setCategoryTotals(
        Object.entries(categories)
          .map(([category, total]) => ({ category, total }))
          .sort((a, b) => b.total - a.total)
      );
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const groupExpensesByDate = () => {
    const grouped = {};
    expenses.forEach(expense => {
      const date = expense.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(expense);
    });
    return grouped;
  };

  const groupedExpenses = groupExpensesByDate();

  return (
    <div className="p-4 space-y-6 animate-in pb-24">
      {/* Header */}
      <div className="pt-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">
          History
        </h1>
        <p className="text-muted-foreground mt-1">Your spending over time</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="daily" className="w-full">
        <TabsList data-testid="history-tabs" className="grid w-full grid-cols-3 bg-secondary">
          <TabsTrigger value="daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly">Weekly</TabsTrigger>
          <TabsTrigger value="category">Category</TabsTrigger>
        </TabsList>

        {/* Daily Breakdown */}
        <TabsContent value="daily" className="space-y-4 mt-6">
          {Object.entries(groupedExpenses).length === 0 ? (
            <p data-testid="no-expenses-message" className="text-center text-muted-foreground py-8">
              No expenses yet
            </p>
          ) : (
            Object.entries(groupedExpenses).map(([date, dayExpenses]) => {
              const total = dayExpenses.reduce((sum, exp) => sum + exp.amount, 0);
              return (
                <Card key={date} data-testid={`daily-expense-${date}`} className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-center">
                      <span>{format(new Date(date), 'MMM dd, yyyy')}</span>
                      <span className="text-primary">${total.toFixed(2)}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {dayExpenses.map((expense, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center py-2 border-b border-border last:border-0"
                      >
                        <div>
                          <p className="font-medium text-foreground">{expense.category}</p>
                          {expense.note && (
                            <p className="text-sm text-muted-foreground">{expense.note}</p>
                          )}
                        </div>
                        <p className="font-semibold">${expense.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Weekly Totals */}
        <TabsContent value="weekly" className="space-y-4 mt-6">
          {weeklyTotals.map((week, idx) => (
            <Card key={idx} data-testid={`weekly-total-${idx}`} className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>Week of {week.week}</span>
                  <span className="text-primary">${week.total.toFixed(2)}</span>
                </CardTitle>
              </CardHeader>
            </Card>
          ))}
        </TabsContent>

        {/* Category Totals */}
        <TabsContent value="category" className="space-y-4 mt-6">
          {categoryTotals.map((cat, idx) => (
            <Card key={idx} data-testid={`category-total-${cat.category}`} className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{cat.category}</span>
                  <span className="text-primary">${cat.total.toFixed(2)}</span>
                </CardTitle>
              </CardHeader>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HistoryScreen;